import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-20 px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '亚洲国产高清影视内容精选平台',
    },
    publisher: {
      '@type': 'Organization',
      name: '亚洲国产高清影视内容精选平台',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://loforce.cn/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://loforce.cn', name: '首页' } },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `https://loforce.cn/category/${article.category}`,
          name: category?.name || article.category,
        },
      },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://loforce.cn/article/${article.id}`, name: article.title } },
    ],
  };

  const getCategoryName = (slug: string) =>
    categories.find((c) => c.slug === slug)?.name || slug;

  return (
    <>
      <SEOHead
        title={`${article.title} | 亚洲国产高清影视内容精选平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://loforce.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={[articleSchema, breadcrumbSchema]}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category?.name || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-label font-medium rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {getCategoryName(article.category)}
                </Link>
                <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-label rounded-full whitespace-nowrap">
                  {article.type}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(0)}万次阅读` : `${article.viewCount}次阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {article.rating} 分
                </span>
                <span>{article.region}</span>
                <span>{article.year}</span>
              </div>

              <div className="aspect-video rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-50 transition-all duration-300"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-white text-xs font-label font-medium">{related.rating}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-950 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2">{related.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}